import { useMemo } from 'react';
import get from 'lodash/get';
import { ProductForm, RefData } from 'types/interfaces';
import { KycQuestion } from './types';
import { KycQuestionKeys } from './constants';
import { resolveKycTitle, resolveKycRefData } from './utils';

export type KycQuestionData = {
  title: string;
  /** Dropdown choices mapped from `possibleAnswers` into the `RefData[]` format. */
  refData: RefData[] | undefined;
};

type UseKycQuestionsProps<F extends string> = {
  /**
   * Maps a form/variable field name to its BFF KYC question code.
   * e.g. `{ fundingSource: KycQuestionCodes.mainSourceOfFunds }` or the shared `ACCOUNT_KYC_FIELD_TO_CODE` or `INVESTOR_KYC_FIELD_TO_CODE` constant.
   */
  fieldMap: Record<F, string>;
  productForm: Pick<ProductForm, 'values'> | undefined;
  locale: string;
  /** Fallback label per field — defaults to empty string when the BFF does not return that question. */
  fallbackTitles?: Partial<Record<F, string>>;
  valuesKey?: KycQuestionKeys;
};

/**
 * Resolves KYC question data (title + dropdown choices) for one or more fields in a single call.
 * Returns a `Record<F, KycQuestionData>` keyed by field name — every entry is always present,
 * falling back to the provided label (or empty string) when the BFF omits a question.
 *
 * Usage on a form step:
 *   const { fundingSource } = useKycQuestions({
 *     fieldMap: { fundingSource: KycQuestionCodes.mainSourceOfFunds },
 *     productForm,
 *     locale: intl.locale,
 *     fallbackTitles: { fundingSource: getText('fundingSource') },
 *   });
 *
 * Usage on the summary (title-only, no fallbacks needed):
 *   const kycData = useKycQuestions({ fieldMap: ACCOUNT_KYC_FIELD_TO_CODE or INVESTOR_KYC_FIELD_TO_CODE, productForm, locale });
 *   const kycLabels = Object.fromEntries(Object.entries(kycData).map(([k, v]) => [k, v.title]));
 */
export const useKycQuestions = <F extends string>({
  fieldMap,
  productForm,
  locale,
  fallbackTitles,
  valuesKey,
}: UseKycQuestionsProps<F>): Record<F, KycQuestionData> => {
  const kycQuestions = valuesKey
    ? (get(productForm, ['values', valuesKey]) as Record<string, KycQuestion> | undefined)
    : undefined;

  return useMemo(() => {
    const fields = Object.keys(fieldMap) as F[];
    return Object.fromEntries(
      fields.map((field) => {
        const question = kycQuestions?.[fieldMap[field]];
        return [
          field,
          {
            title: resolveKycTitle(question, locale, fallbackTitles?.[field] ?? ''),
            refData: resolveKycRefData(question),
          },
        ];
      }),
    ) as Record<F, KycQuestionData>;
  }, [fieldMap, fallbackTitles, kycQuestions, locale]);
};
